import React from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Divider,
} from '@mui/material'
import { ArrowBack } from '@mui/icons-material'

const Terms: React.FC = () => {
  const navigate = useNavigate()

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="md">
        <Button 
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 3 }}
        >
          Back to Home
        </Button>

        <Paper sx={{ p: 4 }}>
          <Typography variant="h3" fontWeight="bold" gutterBottom>
            Terms of Service
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Last Updated: October 2025
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            1. Acceptance of Terms
          </Typography>
          <Typography paragraph>
            By accessing or using BlueBit TrialFinder ("the Service"), you agree to be bound by these Terms of Service 
            ("Terms"). If you do not agree to these Terms, do not use the Service. BlueBit Health LLC ("we," "our," or "us") 
            reserves the right to modify these Terms at any time.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            2. Medical Disclaimer
          </Typography>
          <Typography paragraph sx={{ fontWeight: 'bold', color: 'error.main', bgcolor: 'error.lighter', p: 2, borderRadius: 1 }}>
            IMPORTANT: This Service is NOT a substitute for professional medical advice, diagnosis, or treatment. Always 
            seek the advice of your physician or other qualified health provider with any questions you may have regarding 
            a medical condition. Never disregard professional medical advice or delay in seeking it because of information 
            obtained through this Service.
          </Typography>
          <Typography paragraph>
            The Service provides information about clinical trials but does not:
          </Typography>
          <Typography component="ul">
            <li>Provide medical advice or diagnosis</li>
            <li>Recommend specific treatments or clinical trials</li>
            <li>Replace consultation with healthcare professionals</li>
            <li>Guarantee eligibility for any clinical trial</li>
            <li>Endorse any particular clinical trial or research study</li>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            3. Service Description
          </Typography>
          <Typography paragraph>
            BlueBit TrialFinder is a health information management and clinical trial search tool that:
          </Typography>
          <Typography component="ul">
            <li>Helps users organize and track their health information</li>
            <li>Provides AI-assisted data entry through photos, voice, and text</li>
            <li>Enables searching of the ClinicalTrials.gov database</li>
            <li>Displays information about clinical trials that may match user's health conditions</li>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            4. User Accounts and Eligibility
          </Typography>
          <Typography paragraph>
            To use the Service, you must:
          </Typography>
          <Typography component="ul">
            <li>Be at least 13 years of age (or 18 in some jurisdictions)</li>
            <li>Provide accurate and complete registration information</li>
            <li>Maintain the security of your account credentials</li>
            <li>Accept responsibility for all activities under your account</li>
            <li>Notify us immediately of any unauthorized access</li>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            5. Privacy and Personal Health Information
          </Typography>
          <Typography paragraph>
            Your use of the Service is also governed by our Privacy Policy. By using the Service, you consent to our 
            collection and use of information as described in the Privacy Policy.
          </Typography>
          <Typography paragraph sx={{ fontWeight: 'bold', color: 'warning.main' }}>
            PRIVACY WARNING: When entering health information or taking photos of medical documents, please EXCLUDE or 
            MASK personal identifiers such as your full name, address, Social Security Number, date of birth, and medical 
            record numbers. Only include medical information relevant to searching for clinical trials.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            6. Subscription and Payment Terms
          </Typography>
          
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            6.1 Free Tier
          </Typography>
          <Typography component="ul">
            <li>Includes 100 AI API calls at no charge</li>
            <li>Access to basic features</li>
            <li>Subject to usage limitations</li>
          </Typography>

          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            6.2 Premium Subscription
          </Typography>
          <Typography component="ul">
            <li>One-time payment of $20 for lifetime access</li>
            <li>Additional API credits available for purchase</li>
            <li>All payments processed through Apple App Store or authorized payment processors</li>
            <li>No refunds for completed AI API calls or processed transactions</li>
          </Typography>

          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            6.3 Refunds
          </Typography>
          <Typography paragraph>
            Refunds for subscription purchases may be requested within 14 days of purchase if no substantial use of 
            premium features has occurred. Refund requests should be directed to info@bluebithealth.com or through 
            the Apple App Store refund process.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            7. Acceptable Use Policy
          </Typography>
          <Typography paragraph>
            You agree NOT to:
          </Typography>
          <Typography component="ul">
            <li>Use the Service for any illegal purpose</li>
            <li>Share account credentials with others</li>
            <li>Attempt to reverse engineer or hack the Service</li>
            <li>Upload malicious code or viruses</li>
            <li>Scrape or automatically collect data from the Service</li>
            <li>Misrepresent your identity or affiliation</li>
            <li>Use the Service to spam or harass others</li>
            <li>Violate any applicable laws or regulations</li>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            8. Intellectual Property
          </Typography>
          <Typography paragraph>
            The Service, including all content, features, and functionality, is owned by BlueBit Health LLC and is 
            protected by United States and international copyright, trademark, patent, trade secret, and other 
            intellectual property laws.
          </Typography>
          <Typography paragraph>
            You retain ownership of your health data. By using the Service, you grant us a limited license to process 
            your data solely for the purpose of providing the Service to you.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            9. Third-Party Services
          </Typography>
          <Typography paragraph>
            The Service integrates with third-party services including:
          </Typography>
          <Typography component="ul">
            <li><strong>OpenAI API:</strong> For AI-powered data processing</li>
            <li><strong>ClinicalTrials.gov:</strong> For clinical trial information</li>
            <li><strong>Apple App Store:</strong> For payment processing</li>
          </Typography>
          <Typography paragraph>
            Your use of these third-party services is subject to their respective terms of service and privacy policies. 
            We are not responsible for the practices or content of third-party services.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            10. Disclaimers and Limitations of Liability
          </Typography>
          
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            10.1 No Warranties
          </Typography>
          <Typography paragraph>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, 
            INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
          </Typography>

          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            10.2 Accuracy of Information
          </Typography>
          <Typography paragraph>
            We do not guarantee the accuracy, completeness, or usefulness of any information provided through the Service, 
            including AI-generated content and clinical trial information. Clinical trial data is sourced from 
            ClinicalTrials.gov and may not be current or complete.
          </Typography>

          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mt: 2 }}>
            10.3 Limitation of Liability
          </Typography>
          <Typography paragraph>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, BLUEBIT HEALTH LLC SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
            SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY 
            OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM:
          </Typography>
          <Typography component="ul">
            <li>Your use or inability to use the Service</li>
            <li>Any unauthorized access to your data</li>
            <li>Any errors or omissions in Service content</li>
            <li>Decisions made based on information from the Service</li>
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            11. Indemnification
          </Typography>
          <Typography paragraph>
            You agree to indemnify and hold harmless BlueBit Health LLC, its officers, directors, employees, and agents 
            from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your use of 
            the Service or violation of these Terms.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            12. Termination
          </Typography>
          <Typography paragraph>
            We reserve the right to suspend or terminate your access to the Service at any time, with or without cause, 
            with or without notice. Upon termination, your right to use the Service will immediately cease. You may 
            terminate your account at any time by contacting us at info@bluebithealth.com.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            13. Governing Law and Dispute Resolution
          </Typography>
          <Typography paragraph>
            These Terms shall be governed by the laws of the State of [Your State], United States, without regard to 
            its conflict of law provisions. Any disputes arising from these Terms or the Service shall be resolved through 
            binding arbitration in accordance with the American Arbitration Association's rules.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            14. Changes to Terms
          </Typography>
          <Typography paragraph>
            We reserve the right to modify these Terms at any time. We will notify users of material changes by posting 
            the updated Terms on this page with a new "Last Updated" date. Your continued use of the Service after 
            changes constitutes acceptance of the modified Terms.
          </Typography>

          <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mt: 4 }}>
            15. Contact Information
          </Typography>
          <Typography paragraph>
            For questions about these Terms, contact us at:
          </Typography>
          <Typography component="div" sx={{ ml: 2 }}>
            <strong>BlueBit Health LLC</strong><br />
            Email: info@bluebithealth.com<br />
            Website: www.bluebithealth.com
          </Typography>

          <Divider sx={{ my: 4 }} />

          <Typography variant="body2" color="text.secondary" align="center">
            © 2025 BlueBit Health LLC. All rights reserved.
          </Typography>
        </Paper>
      </Container>
    </Box>
  )
}

export default Terms

